import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReportsService, DaySales, TopProduct } from '../../services/reports/reports.service';
import { StoreService, StoreOption } from '../../services/stores/store.service';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { formatIndianCompactCurrency, formatIndianCompactNumber } from '../../utils/indian-compact-number';

interface KPIMetric { label: string; value: string; trend: string; trendUp: boolean; icon: string; color: string; }
@Component({
  selector: 'app-analytics-dashboard', standalone: true, imports: [CommonModule, FormsModule],
  templateUrl: './analytics-dashboard.component.html',
})
export class AnalyticsDashboardComponent implements OnInit {
  private reportsService = inject(ReportsService);
  private storesService = inject(StoreService);
  isLoading = signal(true);
  error = signal('');
  selectedStore = signal('all');
  selectedTimeRange = signal<'today' | '7d' | '30d' | '90d' | 'custom'>('7d');
  customFrom = '';
  customTo = '';
  private fetchVersion = 0;
  kpiMetrics = signal<KPIMetric[]>([]);
  salesTrends = signal<DaySales[]>([]);
  topProducts = signal<TopProduct[]>([]);
  stores = signal<StoreOption[]>([]);
  gmv = computed(() => this.salesTrends().reduce((acc, curr) => acc + curr.revenue, 0));
  totalOrders = computed(() => this.salesTrends().reduce((acc, curr) => acc + curr.orders, 0));
  aov = computed(() => this.totalOrders() > 0 ? this.gmv() / this.totalOrders() : 0);
  ngOnInit() { this.fetchAnalytics(); }
  fetchAnalytics() {
    const version = ++this.fetchVersion;
    this.error.set('');
    const days = this.selectedTimeRange() === 'today' ? 1 : this.selectedTimeRange() === '7d' ? 7 : this.selectedTimeRange() === '90d' ? 90 : 30;
    const range = this.selectedTimeRange() === 'custom'
      ? { from: new Date(this.customFrom + 'T00:00:00+05:30'), to: new Date(this.customTo + 'T23:59:59.999+05:30') }
      : this.reportsService.getRange(days);
    if (!Number.isFinite(range.from.getTime()) || !Number.isFinite(range.to.getTime()) || range.to < range.from) {
      this.error.set('Select a valid start and end date.');
      return;
    }
    const duration = range.to.getTime() - range.from.getTime() + 1;
    const previous = { from: new Date(range.from.getTime() - duration), to: new Date(range.from.getTime() - 1) };
    const store = this.selectedStore() === 'all' ? undefined : this.selectedStore();
    this.isLoading.set(true);
    forkJoin({
      trends: this.reportsService.getSalesPerformance(days, store, range),
      products: this.reportsService.getTopProducts(5, days, store, range),
      metrics: this.reportsService.getBusinessMetrics(range, store),
      previous: this.reportsService.getBusinessMetrics(previous, store),
      storeList: this.storesService.allStores(),
    }).pipe(finalize(() => { if (version === this.fetchVersion) this.isLoading.set(false); })).subscribe({
      next: res => {
        if (version !== this.fetchVersion) return;
        this.salesTrends.set(res.trends);
        this.topProducts.set(res.products);
        this.stores.set(res.storeList);
        this.updateKPIs(res.metrics, res.previous);
      },
      error: () => {
        if (version !== this.fetchVersion) return;
        this.kpiMetrics.set([]); this.salesTrends.set([]); this.topProducts.set([]);
        this.error.set('Could not load verified analytics. Check your connection and retry.');
      },
    });
  }
  private updateKPIs(current: Record<string, number | null>, previous: Record<string, number | null>) {
    const definitions = [
      { key: 'placed_gmv', label: 'Placed GMV', currency: true },
      { key: 'fulfilled_sales', label: 'Fulfilled Sales', currency: true },
      { key: 'net_sales', label: 'Net Sales', currency: true },
      { key: 'refunded_amount', label: 'Refunds completed', currency: true },
      { key: 'delivered_orders', label: 'Delivered Orders', currency: false },
      { key: 'aov', label: 'Delivered AOV', currency: true },
      { key: 'cancellation_rate', label: 'Cancellation Rate', currency: false },
    ];
    this.kpiMetrics.set(definitions.map(definition => {
      const value = current[definition.key], before = previous[definition.key];
      const change = value == null || before == null || before === 0 ? null : (value - before) * 100 / Math.abs(before);
      return {
        label: definition.label,
        value: value == null ? 'Not available' : definition.key === 'cancellation_rate' ? `${Number(value).toFixed(1)}%`
          : definition.currency ? formatIndianCompactCurrency(value) : formatIndianCompactNumber(value),
        trend: change == null ? 'Not available' : `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
        trendUp: change != null && (definition.key === 'cancellation_rate' ? change <= 0 : change >= 0),
        icon: 'fa-solid fa-chart-line', color: 'primary',
      };
    }));
  }
  getTrendPath() {
    if (this.salesTrends().length < 2) return '';
    const maxRevenue = Math.max(...this.salesTrends().map(s => s.revenue), 1);
    const step = 1000 / (this.salesTrends().length - 1);
    return this.salesTrends().map((s, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${200 - s.revenue / maxRevenue * 200}`).join(' ');
  }
}
